import { authFetch, API } from './api';

// Shared label delivery: talks to the local Zebra Browser Print service when
// present + enabled, otherwise renders the merged PDF in a window and opens the
// print dialog. Used by both bulk Print Labels (App) and single-label Reprint
// (OrderDetailPanel) so the two never drift. Every local-printer call is
// time-boxed — a missing/offline/stuck printer can NEVER block printing; it
// just falls through to the PDF tab.
//
// PDF presentation philosophy (2026-09-03): a toolbar, not detection. We never
// try to work out whether the browser rendered or downloaded a PDF — that
// heuristic replaced a perfectly rendered label with a "downloaded" notice on
// a render-mode Chrome (its PDF viewer in an iframe looks empty from outside).
// Every popup simply carries Print + Save buttons; each browser mode has a
// working path and nothing ever replaces content after the fact.

export function fetchTimeout(url: string, opts: RequestInit, ms: number) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// The local Zebra Browser Print device (127.0.0.1:9100), or null if absent.
export async function detectZebra(): Promise<any | null> {
  try {
    const r = await fetchTimeout('http://127.0.0.1:9100/default?type=printer', {}, 2000);
    if (!r.ok) return null;
    const txt = await r.text();
    if (!txt || !txt.trim().startsWith('{')) return null;
    const dev = JSON.parse(txt);
    return dev && dev.uid ? dev : null;
  } catch { return null; }
}

// Only look for a local printer when direct printing is enabled in Settings.
export async function detectZebraIfEnabled(): Promise<any | null> {
  try {
    const sres = await authFetch(`${API}/label-print-settings`);
    const s = sres.ok ? await sres.json() : {};
    if (s.zebra_direct === false) return null;
  } catch { /* settings unreachable → still try, it's harmless */ }
  return detectZebra();
}

// Send ZPL to Browser Print in small batches. One giant write is how a
// 40-label run printed NOTHING on 2026-08-31: 4.6 MB in a single 8 s-boxed
// request, the box aborted mid-body, and the agent discards an incomplete
// request. _pdf_to_zpl emits one ^XA..^XZ block per label, so the stream
// splits cleanly and each batch is small enough to finish inside its box.
// Returns how many labels the agent accepted.
const ZPL_BATCH_LABELS = 5;
const ZPL_BATCH_TIMEOUT_MS = 20000;

async function writeZplBatches(zebra: any, zpl: string): Promise<{ sent: number; total: number }> {
  const labels = zpl.split('^XZ').map(s => s.trim()).filter(Boolean).map(s => s + '^XZ');
  let sent = 0;
  for (let i = 0; i < labels.length; i += ZPL_BATCH_LABELS) {
    const chunk = labels.slice(i, i + ZPL_BATCH_LABELS);
    try {
      const w = await fetchTimeout('http://127.0.0.1:9100/write', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ device: zebra, data: chunk.join('\n') }),
      }, ZPL_BATCH_TIMEOUT_MS);
      if (!w.ok) break;
    } catch { break; }
    sent += chunk.length;
  }
  return { sent, total: labels.length };
}

// How to switch a browser back to opening PDFs inline — shown as the hint's
// tooltip in the popup toolbar. Keep it free of quotes and newlines: it is
// interpolated into a title="…" attribute.
export const PDF_SETTINGS_HELP =
  'To preview PDFs in this window instead: Edge → Settings → Cookies and site permissions → '
  + 'PDF documents → turn off Always download PDF files. Chrome → Settings → Privacy → '
  + 'Site settings → PDF documents → Open PDFs in Chrome.';

// ── the one PDF popup ─────────────────────────────────────────────────────────
// Toolbar + SERVER-RENDERED page images. The popup deliberately never embeds
// the PDF itself, because the browser's PDF viewer is the unreliable part:
// a browser set to "download PDFs" (Henrik's, kept on purpose) renders nothing
// in an iframe, and on 2026-09-03 every attempt to DETECT that state failed in
// both directions (Chrome's viewer iframe looks empty from outside — a rendered
// label got replaced by a "downloaded" notice; gesture-less iframe downloads
// get silently blocked — the notice claimed a file that never arrived). Never
// reintroduce viewer embedding or DOM-sniffing here.
//
// Instead the popup shows SERVER-rendered page images — identical in every
// browser mode — printed with mm-exact CSS (@page sized from the PDF's own
// mediabox; the server rasterizes with the same pdftoppm that feeds the Zebra
// path, which is proven to keep barcodes scannable). Save PDF hands over the
// original file; a click is a user gesture, so that download always works
// whatever the browser's PDF setting.
//
// Two ways to get the images:
//   showPdfInPopup — posts the PDF back to /api/pdf-preview and receives every
//     page in one JSON. Fine for a label run (small, few pages); used by labels.
//   showDocInPopup — the server kept the PDF it just produced (X-Doc-Token on
//     the response) and renders pages on request, /api/docs/{token}/page/N.png,
//     fetched a few at a time. The post-back path capped a run at 60 pages and
//     returned ~100 A4 pages as one 30–50 MB JSON, which is what broke the
//     50-order delivery-note preview (Fernando, 2026-09-06). Pages are fetched
//     with authFetch (the API wants a bearer header, which <img src> cannot
//     carry) and shown from blob URLs, so nothing is held as base64 text.
//
// title/filename are interpolated into document.write HTML — pass compile-time
// constants only, never user or order data.

type PopupUi = {
  doc: Document;
  pages: HTMLElement;
  status: (msg: string) => void;
  addPageStyle: (widthMm: number, heightMm: number) => void;
  markReady: () => void;
};

// The shared shell: toolbar (Print / Save PDF / hint), status line, empty page
// holder, print button gated on `ready`. Returns null when the window is gone.
function preparePopup(win: Window | null, blobUrl: string,
                      opts: { title: string; filename: string; autoPrint: boolean }): PopupUi | null {
  if (!win || win.closed) return null;
  const { title, filename, autoPrint } = opts;
  try {
    win.document.open();
    win.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>`
      + '<style>'
      + '*{margin:0;padding:0;box-sizing:border-box}'
      + 'body{font-family:sans-serif;background:#4b5563;padding-top:44px}'
      + '#bar{position:fixed;top:0;left:0;right:0;height:44px;display:flex;align-items:center;'
      + 'gap:.75rem;padding:0 .75rem;background:#1f2937;color:#e5e7eb;z-index:2}'
      + '#bar button,#bar a{background:#1a56db;color:#fff;border:none;border-radius:.4rem;'
      + 'padding:.4rem .9rem;font:inherit;font-size:.85rem;font-weight:600;'
      + 'text-decoration:none;cursor:pointer}'
      + '#hint{font-size:.78rem;color:#9ca3af}'
      + '#pages{padding:1rem}'
      + '#pages img{display:block;margin:0 auto 1rem;background:#fff;max-width:min(100%,820px);'
      + 'height:auto;box-shadow:0 2px 10px rgba(0,0,0,.4)}'
      + '#status{color:#e5e7eb;text-align:center;padding:2rem;font-size:.95rem}'
      + '@media print{#bar,#status{display:none}body{background:#fff;padding:0}'
      + '#pages{padding:0}#pages img{margin:0;box-shadow:none;max-width:none;'
      + 'page-break-after:always}}'
      + '</style></head><body>'
      + '<div id="bar">'
      + '<button id="print" type="button">🖨 Print</button>'
      + `<a id="save" href="${blobUrl}" download="${filename}">⬇ Save PDF</a>`
      // Open PDF: the same blob in a new tab. In a browser that shows PDFs
      // inline this is instant and prints from the browser's own viewer; in a
      // download-mode browser it downloads, exactly like Save. A click the
      // user makes — not the viewer-sniffing this file rules out. (Fernando,
      // 2026-09-06: the fastest path for a 50-order note run.)
      + `<a id="open" href="${blobUrl}" target="_blank" rel="noopener">↗ Open PDF</a>`
      + `<span id="hint" title="${PDF_SETTINGS_HELP}">Preview is rendered by the WMS — `
      + 'Open PDF uses your browser’s own viewer; Save PDF gives you the file.</span>'
      + '</div>'
      + '<div id="status">Rendering preview…</div>'
      + '<div id="pages"></div>'
      + '</body></html>');
    win.document.close();
  } catch (_) { return null; /* window gone mid-write */ }

  // NOTE: the blob URL is deliberately never revoked — the Save link needs it
  // for the popup's whole life; it dies with the opener document anyway.

  let ready = false;
  const doc = win.document;
  const status = (msg: string) => {
    const el = doc.getElementById('status');
    if (el) { el.textContent = msg; (el as HTMLElement).style.display = msg ? '' : 'none'; }
  };

  doc.getElementById('print')?.addEventListener('click', () => {
    if (!ready) {
      try { win.alert('The preview is not ready — use Save PDF and print the file '
                      + 'from your Downloads folder.'); } catch (_) {}
      return;
    }
    try { win.focus(); win.print(); } catch (_) { /* dialog refused; toolbar remains */ }
  });

  const pages = doc.getElementById('pages') as HTMLElement | null;
  if (!pages) return null;

  return {
    doc, pages, status,
    // mm-exact print sizing off the FIRST page (a run is uniform: labels are one
    // roll size, notes are A4); screen size stays responsive via the stylesheet
    addPageStyle: (w0, h0) => {
      const style = doc.createElement('style');
      style.textContent = `@page{size:${w0}mm ${h0}mm;margin:0}`
        + `@media print{#pages img{width:${w0}mm;height:${h0}mm}}`;
      doc.head.appendChild(style);
    },
    markReady: () => {
      if (win.closed) return;
      status('');
      ready = true;
      if (autoPrint) {
        setTimeout(() => {
          if (win.closed) return;
          try { win.focus(); win.print(); } catch (_) { /* toolbar remains */ }
        }, 150);
      }
    },
  };
}

export function showPdfInPopup(win: Window | null, pdfBase64: string, blobUrl: string,
                               opts: { title: string; filename: string; autoPrint: boolean }) {
  const ui = preparePopup(win, blobUrl, opts);
  if (!ui || !win) return;

  authFetch(`${API}/pdf-preview`, {
    method: 'POST', body: JSON.stringify({ pdf_base64: pdfBase64 }),
  }).then(async r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const pages = (await r.json()).pages || [];
    if (win.closed) return;
    if (!pages.length) { ui.status('Preview unavailable — use Save PDF.'); return; }
    ui.addPageStyle(pages[0].width_mm, pages[0].height_mm);
    const loads: Promise<void>[] = [];
    for (const p of pages) {
      const img = ui.doc.createElement('img');
      loads.push(new Promise(res => { img.onload = () => res(); img.onerror = () => res(); }));
      img.src = `data:image/png;base64,${p.png_base64}`;
      ui.pages.appendChild(img);
    }
    await Promise.all(loads);
    if (win.closed) return;
    ui.markReady();
  }).catch(() => {
    if (!win.closed) ui.status('Preview unavailable — use Save PDF.');
  });
}

// How many page images are fetched at once. Enough to keep the server's
// 10-page render chunks busy, few enough that a slow page never blocks the rest.
const DOC_PAGE_PARALLEL = 4;

export function showDocInPopup(win: Window | null, token: string, blobUrl: string,
                               opts: { title: string; filename: string; autoPrint: boolean }) {
  const ui = preparePopup(win, blobUrl, opts);
  if (!ui || !win) return;
  const base = `${API}/docs/${encodeURIComponent(token)}`;

  authFetch(`${base}/meta`).then(async r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const meta = await r.json();
    const total: number = meta.pages || 0;
    if (win.closed) return;
    if (!total) { ui.status('Preview unavailable — use Save PDF.'); return; }
    ui.addPageStyle(meta.sizes[0].width_mm, meta.sizes[0].height_mm);

    // One slot per page up front, so pages land in order however the fetches
    // finish; an <img> without a src takes no space until its page arrives.
    const slots: HTMLImageElement[] = [];
    for (let i = 0; i < total; i++) {
      const img = ui.doc.createElement('img');
      img.decoding = 'async';
      img.alt = `Page ${i + 1}`;
      ui.pages.appendChild(img);
      slots.push(img);
    }

    let next = 1, done = 0;
    ui.status(`Rendering page 1 of ${total}…`);
    const worker = async () => {
      while (next <= total) {
        const i = next++;
        const pr = await authFetch(`${base}/page/${i}.png`);
        if (!pr.ok) throw new Error(`HTTP ${pr.status}`);
        const blob = await pr.blob();
        if (win.closed) return;
        const img = slots[i - 1];
        await new Promise<void>(res => {
          img.onload = () => res(); img.onerror = () => res();
          img.src = URL.createObjectURL(blob);
        });
        done++;
        ui.status(`Rendering page ${done} of ${total}…`);
      }
    };
    await Promise.all(Array.from({ length: Math.min(DOC_PAGE_PARALLEL, total) }, worker));
    if (win.closed) return;
    ui.markReady();
  }).catch(() => {
    if (!win.closed) ui.status('Preview unavailable — use Save PDF.');
  });
}

// Turn a Blob into base64 without blowing the stack on big files.
async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(bin);
}

// Present a fetched PDF blob in an already-open placeholder popup. The popup
// must have been opened synchronously in the click handler (popup blockers);
// this swaps its placeholder for the toolbar + preview layout. With a docToken
// (the response's X-Doc-Token header) the pages are fetched by reference; the
// blob is then only what Save PDF hands over.
export function presentPdf(win: Window | null, blob: Blob,
                           opts: { title: string; filename: string; autoPrint: boolean },
                           docToken?: string | null) {
  if (!win || win.closed) return;
  const url = URL.createObjectURL(blob);
  if (docToken) { showDocInPopup(win, docToken, url, opts); return; }
  blobToBase64(blob).then(b64 => showPdfInPopup(win, b64, url, opts));
}

// Given a print/reprint response ({merged_pdf_base64, zpl}) and a window opened
// synchronously in the click handler, send ZPL straight to the printer if we
// have a device + zpl, else render the PDF in the window and open the dialog.
// Returns true if everything went to the Zebra printer.
export async function deliverLabel(win: Window | null, data: any, zebra: any): Promise<boolean> {
  if (zebra && data.zpl) {
    const { sent, total } = await writeZplBatches(zebra, data.zpl);
    if (sent === total && total > 0) {
      if (win) win.close();
      return true;
    }
    if (sent > 0) {
      // Partial: the first `sent` labels are already on paper. Falling through
      // to the full PDF would print those twice — say what happened instead.
      if (win) {
        win.document.open();
        win.document.write(
          '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Labels</title></head>'
          + '<body><p style="font-family:sans-serif;padding:2rem;max-width:40em">'
          + `The printer accepted <b>${sent} of ${total}</b> labels, then stopped answering. `
          + 'Check the printer (paper, pause light, the Browser Print icon in the tray), then '
          + 'print the missing labels one at a time with Reprint on their orders — pressing '
          + 'Print again resends the whole stack, so the first ones would come out twice.'
          + '</p></body></html>');
        win.document.close();
      }
      return false;
    }
    console.warn('Browser Print write failed/timed out — falling back to PDF tab');
  }

  if (data.merged_pdf_base64 && win) {
    // The blob URL feeds the popup's Save-PDF link (the original file); the
    // preview itself is server-rendered images — see showPdfInPopup for why
    // the browser's own PDF viewer is never involved.
    const bytes = atob(data.merged_pdf_base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    showPdfInPopup(win, data.merged_pdf_base64, url,
                   { title: 'Labels', filename: 'labels.pdf', autoPrint: true });
  } else if (win) {
    win.close();
  }
  return false;
}
